import React, { useRef, useEffect, useId } from "react";
import ReactDOM from "react-dom";
import { CSSTransition } from "react-transition-group";

import Backdrop from "./Backdrop";
import "./styling/Modal.css";

// Must match the CSS enter/exit duration (--transition-fast: 0.15s) used by the
// .modal-* and .backdrop-* transitions. CSSTransition needs this in JS to know
// how long to keep the node mounted while it animates out.
const TRANSITION_MS = 150;

// Everything that can hold keyboard focus, for the focus trap below.
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(",");

// Shell width scale, defined in Modal.css. `md` reproduces the base .modal box
// exactly, so every caller that passes no `size` is unchanged.
const MODAL_SIZES = ["sm", "md", "lg", "xl"];

const sizeClassFor = (size) => {
  if (size == null || MODAL_SIZES.includes(size)) return `modal--${size || "md"}`;
  // Falling back silently would make a typo ("large", "xxl") look like a
  // deliberate default and cost someone an afternoon.
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.warn(
      `Modal: unknown size "${size}" — using "md". Expected one of: ${MODAL_SIZES.join(", ")}.`,
    );
  }
  return "modal--md";
};

const ModalOverlay = React.forwardRef((props, ref) => {
  const titleId = useId();
  // Callers that render their own chrome (the games, the landing modals) pass
  // headerClass="" and no header text — there is nothing to label or to hang a
  // close button off, so those keep the bare box they had before.
  const hasHeader = props.header !== undefined && props.header !== null && props.header !== "";
  const showClose = hasHeader && props.showClose !== false;
  const footerStart = props.footerStart;

  const content = (
    <div
      ref={ref}
      className={`modal ${sizeClassFor(props.size)} ${props.className || ""}`}
      style={props.style}
      data-lenis-prevent
      role="dialog"
      aria-modal="true"
      aria-labelledby={hasHeader ? titleId : undefined}
      aria-label={hasHeader ? undefined : props.ariaLabel}
    >
      <header className={`modal__header ${props.headerClass}`}>
        <h2 id={hasHeader ? titleId : undefined}>{props.header}</h2>
        {showClose && (
          <button
            type="button"
            className="modal__close"
            onClick={props.onCancel}
            aria-label="Close dialog"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        )}
      </header>

      <div className={`modal__content ${props.contentClass}`}>
        {props.children}
      </div>
      <footer className={`modal__footer ${props.footerClass}`}>
        {/* Left-hand slot for the things that are not actions: a wizard's
            "Step 2 of 4", a Back button, a destructive Delete kept away from
            the primary. Callers used to wrap the whole row in their own div to
            get this, which cost them the footer's mobile stacking — the row is
            one flex child again and never collapses. */}
        {footerStart != null && footerStart !== false && (
          <div className="modal__footer-start">{footerStart}</div>
        )}
        {props.footer}
      </footer>
    </div>
  );
  return ReactDOM.createPortal(content, document.getElementById("modal-hook"));
});

// Open modals, oldest first. Several screens stack a second modal over the
// first (NodeEditModal → PlantTimeSeriesImportModal), and both stay mounted —
// so Escape and the focus trap must apply to the topmost one only.
const modalStack = [];

const Modal = (props) => {
  const nodeRef = useRef(null);
  const backdropRef = useRef(null);
  const returnFocusRef = useRef(null);
  const { show, onCancel, dismissOnEscape } = props;
  // Held in refs, not effect deps. Almost every caller passes an inline arrow
  // for onCancel, so its identity changes on each render — depending on it made
  // the effect below tear down and re-run on every keystroke, and its cleanup
  // fired the focus-restore, kicking the caret out of whatever field was being
  // typed into. The effect now runs once per open/close.
  const onCancelRef = useRef(onCancel);
  const dismissOnEscapeRef = useRef(dismissOnEscape);
  useEffect(() => {
    onCancelRef.current = onCancel;
    dismissOnEscapeRef.current = dismissOnEscape;
  });

  // Disable background scroll when the modal is shown. Also pause Lenis (the
  // smooth-scroll instance on the marketing pages) — otherwise it keeps
  // scrolling the page behind the modal. The modal itself is marked
  // data-lenis-prevent so it scrolls natively.
  useEffect(() => {
    if (props.show) {
      document.body.style.overflow = 'hidden';
      if (window.__lenis) window.__lenis.stop();
    } else {
      document.body.style.overflow = 'unset';
      if (window.__lenis) window.__lenis.start();
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
      if (window.__lenis) window.__lenis.start();
    };
  }, [props.show]);

  // Escape to dismiss, Tab cycling kept inside the dialog, and focus returned
  // to whatever opened it. Registered only while this modal is the topmost.
  useEffect(() => {
    if (!show) return undefined;

    const token = {};
    modalStack.push(token);
    returnFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const isTopmost = () => modalStack[modalStack.length - 1] === token;

    const handleKeyDown = (event) => {
      if (!isTopmost()) return;

      if (event.key === "Escape") {
        // Let a dropdown/date picker inside the modal swallow its own Escape
        // first — those close on keydown and mark the event handled.
        if (event.defaultPrevented) return;
        if (onCancelRef.current && dismissOnEscapeRef.current !== false) {
          event.stopPropagation();
          onCancelRef.current();
        }
        return;
      }

      if (event.key !== "Tab" || !nodeRef.current) return;

      const focusable = Array.from(
        nodeRef.current.querySelectorAll(FOCUSABLE),
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !nodeRef.current.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      const index = modalStack.indexOf(token);
      if (index !== -1) modalStack.splice(index, 1);
      // Only hand focus back once nothing is left above us, otherwise closing
      // an inner modal would yank focus out of the one still open. Also skip it
      // when focus has already moved somewhere outside the dialog — restoring
      // then would steal it from wherever the user actually is.
      const active = document.activeElement;
      const focusWasInside =
        !active ||
        active === document.body ||
        (nodeRef.current && nodeRef.current.contains(active));
      if (modalStack.length === 0 && returnFocusRef.current && focusWasInside) {
        returnFocusRef.current.focus();
      }
    };
  }, [show]);

  return (
    <React.Fragment>
      <CSSTransition
        in={props.show}
        mountOnEnter
        unmountOnExit
        timeout={TRANSITION_MS}
        classNames="backdrop"
        nodeRef={backdropRef}
      >
        <Backdrop
          ref={backdropRef}
          error={props.error ? true : false}
          onClick={props.onCancel}
        />
      </CSSTransition>
      <CSSTransition
        in={props.show}
        mountOnEnter
        unmountOnExit
        timeout={TRANSITION_MS}
        classNames="modal"
        nodeRef={nodeRef}
      >
        <ModalOverlay {...props} ref={nodeRef} />
      </CSSTransition>
    </React.Fragment>
  );
};

export default Modal;
